import {Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Hub, HubId} from '../../../../../../shared/models/hub';
import {Option, OptionGroup} from '../../../../../../shared/models/option';
import {BehaviorSubject, Subscription} from 'rxjs';
import {SortTable, SortTableRow} from '../../../../../../shared/models/sort-table/types';
import {BacnetDevice, BacnetDeviceJS} from '../../../../../../shared/models/bacnet';
import {ShadeToolsService} from '../../../../../../core/services/shade-tools.service';
import {HubsService} from '../../../../../../core/services/hubs.service';
import {DevicesService} from '../../../../../../core/services/devices.service';
import {
  SortTableCell,
  SortTableCellButtons,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../../../../shared/models/sort-table/cell-classes';
import {Device, DeviceType} from '../../../../../../shared/models/device';
import {SortTableComponent} from '../../../../../components/sort-table/sort-table.component';
import {FormsModule} from '@angular/forms';
import {MultichoiceHorizontalComponent} from '../../../../../components/multichoice-horizontal/multichoice-horizontal.component';
import {InfoCircleComponent} from '../../../../../components/info-circle/info-circle.component';
import {PopoverSelectHubsComponent} from '../../../../../components/popover-multi-select/object-types/hubs/popover-select-hubs.component';
import {DrawerGapFromTopComponent} from '../../../../../components/drawer-gap-from-top/drawer-gap-from-top.component';
import {NgClass} from '@angular/common';
import {SpinnerComponent} from '../../../../../components/spinner/spinner.component';
import {PillComponent} from '../../../../../components/pill/pill.component';
import {DialogBoxButtonColor} from '../../../../../../shared/models/dialog-box';

const COL_IDX_MODEL = 0;
const COL_IDX_BUTTON = 1;
const COL_IDX_NET_ADDR = 2;
const COL_IDX_INSTANCE = 3;
const COL_IDX_FIRMWARE = 4;

@Component({
  selector: 'motn-bacnet-device-discovery',
  templateUrl: './bacnet-device-discovery.component.html',
  styleUrls: ['./bacnet-device-discovery.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SortTableComponent,
    FormsModule,
    MultichoiceHorizontalComponent,
    InfoCircleComponent,
    PopoverSelectHubsComponent,
    DrawerGapFromTopComponent,
    NgClass,
    SpinnerComponent,
    PillComponent,
  ],
})
export class BacnetDeviceDiscoveryComponent implements OnInit, OnDestroy {
  INFO_TIMEOUT = 'The timeout setting defines the amount of time before the Motion Hub will stop waiting for a response from BACnet/IP devices when attempting device discovery. Some devices require more time than others to respond due to factors such as device age and link speed. Use this timeout setting to accommodate those cases.';
  INFO_INTERRUPTIONS = 'BACnet device discovery is data-intensive and has the potential to block or inhibit communication on BACnet networks. These inherent protocol limitations can prevent alarms, tiles, and other functionalities that depend on the Motion Hub that is performing the discovery to be blocked until device discovery is finished.';

  @Output() clickedOutside: EventEmitter<null> = new EventEmitter<null>();

  private selectedHub: HubId;
  hubs: Hub[] = [];
  hubMenuOpen: boolean = false;
  timeout: number;
  discovering: boolean = false;
  filteredBacnetDevices: BacnetDeviceJS[] = [];
  searchTerm: string = '';

  table$ = new BehaviorSubject<SortTable>(null);
  private tableRows: { [bacnetDeviceUid: string]: SortTableRow } = {};
  private discoverySub: Subscription;

  /**
   * The array of BACnet devices needs to be maintained carefully, so this serves as
   * a storage spot for this.bacnetDevices, which is a read-only getter property that
   * simply returns this property.
   *
   * @see appendBacnetDevices
   * @see resetBacnetDevices
   * @see bacnetDevices
   */
  private _bacnetDevices: BacnetDeviceJS[] = [];

  get bacnetDevices(): BacnetDeviceJS[] {
    return this._bacnetDevices;
  }


  constructor(
    private hubsService: HubsService,
    private devicesService: DevicesService,
    private shadeTools: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    this.hubsService.fetchUserHubs$().subscribe(
      (data) => {
        this.hubs = data;
      },
    );

    this.devicesService.fetchUserDevices$().subscribe();
  }


  ngOnDestroy() {
    this.stopDiscovery();
  }


  /**
   *
   * @param text
   */
  infoDialog(text: string): void {
    this.shadeTools.infoDialog(text);
  }


  /**
   *
   * @param $event
   */
  onHubChange($event: Option) {
    this.selectedHub = $event.id;

    const cache: BacnetDevice[] = this.hubsService.bacnetDeviceDiscoveryCache[this.selectedHub];

    if (cache) {
      this.appendBacnetDevices(cache);
    } else {
      this.resetBacnetDevices();
    }

    this.filterAndRenderDevices();
  }


  private resetBacnetDevices() {
    this._bacnetDevices = [];
  }


  /**
   * Merges an incoming array of BACnet devices with the current known set of
   * BACnet devices for the selected hub, ensuring no duplicates are added.
   *
   * @param bDevices
   * @private
   */
  private appendBacnetDevices(bDevices: BacnetDeviceJS[]) {
    const existingMotionDevices: Device[] = [];

    for (let deviceId in this.devicesService.sessUserDevices) {
      let device = this.devicesService.sessUserDevices[deviceId];

      if (device.Type === 'bacnet') {
        existingMotionDevices.push(device);
      }
    }

    const keys: string[] = [];
    const current: BacnetDeviceJS[] = [...this.bacnetDevices];

    for (let d of this.bacnetDevices) {
      keys.push(this.makeDeviceUid(d));
    }

    for (let bDevice of bDevices) {
      bDevice.Existing = null;

      for (let existingDevice of existingMotionDevices) {
        if (bDevice.Instance && +existingDevice.Address === bDevice.Instance) {
          if (bDevice.NetAddress && existingDevice.IP === bDevice.NetAddress) {
            if (existingDevice.Hub === this.selectedHub) {
              bDevice.Existing = existingDevice;
            }
          }
        }
      }

      let key = this.makeDeviceUid(bDevice);

      if (!keys.includes(key)) {
        current.push(bDevice);
        keys.push(key);
      }
    }

    this._bacnetDevices = current;
  }


  /**
   * This isn't a UID that is stored anywhere, but rather just one for
   * the various processes here to uniquely track the BACnet device.
   *
   * @param device
   * @private
   */
  private makeDeviceUid(device: BacnetDevice): string {
    return `${device.NetAddress} ${device.Instance}`;
  }


  /**
   *
   * @param timeout
   */
  updateTimeout(timeout: Option): void {
    this.timeout = +timeout.id;
  }


  getCurrentSelectedHub(): Hub {
    return this.hubs.find(h => h.ID === this.selectedHub);
  }


  filterAndRenderDevices(): void {
    let filteredDevices: BacnetDeviceJS[];

    if (this.searchTerm.trim() === '') {
      filteredDevices = this.bacnetDevices;
    } else {
      const searchTerm = this.searchTerm.toLowerCase();

      filteredDevices = this.bacnetDevices.filter((device) => {
        return device.Model.toLowerCase().indexOf(searchTerm) > -1
          || device.Manufacturer.toLowerCase().indexOf(searchTerm) > -1;
      });
    }

    this.filteredBacnetDevices = filteredDevices;
    this.updateTable();
  }


  private updateTable() {
    const table: SortTable = {
      css: {
        'min-width': '950px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    {
      table.columns[COL_IDX_MODEL] = {
        name: 'Model/Manufacturer',
        css: {
          'width': '200px',
        },
        sortingFn: SortTableCellTitleDescription.sortingFnTitle,
        sortDefault: 'up',
      };

      table.columns[COL_IDX_BUTTON] = {
        name: '',
        css: {
          'width': 'calc(100% - 750px)',
          'min-width': '200px',
          // 'justify-content': 'center',
        },
        // sortingFn: SortTableCell.sortingFnCustomSortValue,
        // sortDefault: 'up',
      };

      table.columns[COL_IDX_NET_ADDR] = {
        name: 'Net Address',
        css: {
          'width': '200px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_INSTANCE] = {
        name: 'Instance',
        css: {
          'width': '100px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_FIRMWARE] = {
        name: 'Firmware',
        css: {
          'width': '250px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };
    }

    for (let device of this.filteredBacnetDevices) {
      const uid = this.makeDeviceUid(device);

      const row: SortTableRow = {
        context: uid,
        cells: [],
      };

      this.upsertRowData(row, device);
      this.tableRows[uid] = row;
      table.rows.push(row);
    }

    this.table$.next(table);
  }


  private upsertRowData(row: SortTableRow, device: BacnetDeviceJS) {
    let action: SortTableCell;
    let actionSortingFnString: string; // only used by sorting function below

    if (!this.canBeAdded(device)) {
      actionSortingFnString = 'can\'t';

      action = new SortTableCellButtons([]);
    } else if (device.Existing) {
      // actionSortingFnString = 'already added';

      const a: SortTableCellTextPill = new SortTableCellTextPill(device.Existing.Name, '' + device.Existing.ID)
        .setMainTextColor('green')
        .setPillColor('green')
        .setPillLetter('d')
        .setShowPillFirst(true);

      action = a;
    } else {
      actionSortingFnString = 'add';

      action = new SortTableCellButtons([{
        id: 'add',
        text: 'Add Device',
        callback: () => {
          this.shadeTools.startDialog(
            'Add Device',
            'Provide a name for this device to identify the device throughout the Motion Platform.',
            [
              {
                text: 'Add Device',
                callback: (deviceName) => {
                  this.devicesService.createDevice$({
                    Address: '' + device.Instance,
                    Type: DeviceType.BACnet,
                    Hub: this.selectedHub,
                    IP: device.NetAddress,
                    Name: deviceName,
                    Owner: this.hubs.find(h => h.ID === this.selectedHub).Owner,
                  }).subscribe(
                    (d) => {
                      device.Existing = d;
                      this.upsertRowData(row, device);
                      this.shadeTools.closeAll();
                    },
                    (err) => {
                      this.shadeTools.errorDialog(err);
                    },
                  );
                },
                callbackIsDefault: true,
                color: DialogBoxButtonColor.Green,
              },
              {
                text: 'Cancel',
                callback: () => {
                  this.shadeTools.closeAll();
                },
                color: DialogBoxButtonColor.Alt,
              },
            ],
            true,
            false,
            false,
            true,
          );
        },
        colors: ['white', 'indigo'],
        size: 'small',
      }]);
    }

    row.cells[COL_IDX_MODEL] = new SortTableCellTitleDescription(device.Model ? device.Model : 'Unknown', device.Manufacturer);
    row.cells[COL_IDX_BUTTON] = action.addSortValue(actionSortingFnString);
    row.cells[COL_IDX_NET_ADDR] = new SortTableCellTextPill(device.NetAddress, '', true);
    row.cells[COL_IDX_INSTANCE] = new SortTableCellTextPill('' + device.Instance, '', true);
    row.cells[COL_IDX_FIRMWARE] = new SortTableCellTextPill(device.Firmware, '');
  }


  /**
   * For preventing known exceptional devices from being addable in the discovery
   * results UI
   *
   * @param device
   */
  canBeAdded(device: BacnetDevice): boolean {
    if (device.Manufacturer === 'Motion LLC') {
      return false;
    }

    // add more filters here

    return true;
  }


  /**
   *
   */
  startDiscovery(): void {
    if (this.discovering) {
      return;
    }

    if (!this.selectedHub) {
      this.shadeTools.errorDialog('Select a hub first and then try again.');
      return;
    }

    this.discovering = true;

    const hub = this.hubs.find((h) => h.ID === this.selectedHub);

    this.discoverySub = this.hubsService.discoverDevicesBacnet$(hub.ID, this.timeout).subscribe(
      (devices) => {
        this.discovering = false;
        this.appendBacnetDevices(devices);
        this.filterAndRenderDevices();
      },
      () => {
        this.discovering = false;
        this.filterAndRenderDevices();
        this.shadeTools.simpleOkDialog('Discovery Failed', 'An error was encountered while attempting device discovery. Verify the hub is connected and then try again.');
      },
    );
  }


  /**
   *
   */
  stopDiscovery(): void {
    this.discoverySub?.unsubscribe();
    this.discovering = false;
  }


  timeoutOptions: OptionGroup = new OptionGroup([
    {
      id: '20',
      name: '20 seconds',
      callback: () => {
        this.timeout = 20;
      },
      selected: true,
    },
    {
      id: '40',
      name: '40 seconds',
      callback: () => {
        this.timeout = 40;
      },
      selected: false,
    },
    {
      id: '60',
      name: '1 minute',
      callback: () => {
        this.timeout = 60;
      },
      selected: false,
    },
    {
      id: '120',
      name: '2 minutes',
      callback: () => {
        this.timeout = 120;
      },
      selected: false,
    },
    {
      id: '300',
      name: '5 minutes',
      callback: () => {
        this.timeout = 300;
      },
      selected: false,
    },
    {
      id: '600',
      name: '10 minutes',
      callback: () => {
        this.timeout = 600;
      },
      selected: false,
    },
  ]);
}
